<script setup lang="ts">
import {computed, useTemplateRef, onMounted, watch} from 'vue';
import $ from 'jquery';

import {setDiffLayers} from './diff-layer-utils.ts';
import {useDiffToggleStore} from '../../allspice_app/stores/diffToggle.ts';
import {useAllSpiceAppStore} from '../../allspice_app/store.ts';
import {saveDiffFilterState} from '../../allspice_app/diffFilterPersistence.ts';

import {SvgIcon} from '../../svg.ts';

const diffToggleStore = useDiffToggleStore();

type DiffToggleOptionKey = 'full' | 'added' | 'removed' | 'no';
type DiffToggleOption = {
  key: DiffToggleOptionKey,
  label: string;
  showAddedChanged: boolean;
  showRemoved: boolean;
};

const props = withDefaults(defineProps<{
  fullDiffLabel?: string;
  addedChangedLabel?: string;
  removedLabel?: string;
  noDiffLabel?: string;
}>(), {
  fullDiffLabel: 'Full Diff',
  addedChangedLabel: 'Added or Changed',
  removedLabel: 'Removed',
  noDiffLabel: 'No Diff',
});

const model = defineModel<DiffToggleOptionKey>({
  default: 'full',
});

const dropdownOptions = computed<Record<DiffToggleOptionKey, DiffToggleOption>>(() => {
  return {
    full: {key: 'full', label: props.fullDiffLabel, showAddedChanged: true, showRemoved: true},
    added: {key: 'added', label: props.addedChangedLabel, showAddedChanged: true, showRemoved: false},
    removed: {key: 'removed', label: props.removedLabel, showAddedChanged: false, showRemoved: true},
    no: {key: 'no', label: props.noDiffLabel, showAddedChanged: false, showRemoved: false},
  };
});

const diffToggleRoot = useTemplateRef('diff-toggle-root');

function applyDiffLayers(showAddedChanged: boolean, showRemoved: boolean) {
  const diffFileBox = diffToggleRoot.value?.closest('.diff-file-box');
  if (diffFileBox) {
    setDiffLayers(diffFileBox, showAddedChanged, showRemoved);
  }
}

function handleOptionSelect(option: DiffToggleOption) {
  model.value = option.key;
  applyDiffLayers(option.showAddedChanged, option.showRemoved);

  // Sync to Pinia store (keeps sidebar in sync)
  diffToggleStore.select(option.key);

  // Persist the toggle state
  const store = useAllSpiceAppStore();
  if (store?.diffFilterConfig) {
    store.diffFilterConfig.diffToggle.value = option.key;
    saveDiffFilterState(store);
  }
}

function refreshDiffToggle() {
  const selectedOption = dropdownOptions.value[model.value];
  applyDiffLayers(selectedOption.showAddedChanged, selectedOption.showRemoved);
}

function diffToVisible() {
  const selectedOption = dropdownOptions.value[model.value];
  return selectedOption.showAddedChanged;
}

function diffFromVisible() {
  const selectedOption = dropdownOptions.value[model.value];
  return selectedOption.showRemoved;
}

// Keep local model in sync when the Pinia store is reset externally
// (e.g. banner Reset button calls diffToggleStore.select('full')).
watch(() => diffToggleStore.selectedKey, (storeKey) => {
  if (model.value !== storeKey) {
    model.value = storeKey;
    applyDiffLayers(
      dropdownOptions.value[storeKey].showAddedChanged,
      dropdownOptions.value[storeKey].showRemoved,
    );
  }
});

defineExpose({
  refreshDiffToggle,
  diffToVisible,
  diffFromVisible,
});

const diffToggleDropdown = useTemplateRef('diff-toggle-dropdown');
onMounted(() => {
  $(diffToggleDropdown.value).dropdown();

  // Restore saved toggle state from persistence
  const store = useAllSpiceAppStore();
  if (store?.diffFilterConfig) {
    const savedToggle = store.diffFilterConfig.diffToggle.value;
    if (savedToggle in dropdownOptions.value) {
      model.value = savedToggle;
      diffToggleStore.select(savedToggle);
    }
  }
});
</script>

<template>
  <div
    ref="diff-toggle-root"
    class="allspice-diff-toggle"
  >
    <div
      ref="diff-toggle-dropdown"
      :class="[
        'ui dropdown scrolling basic button tiny',
        { 'disabled': !diffToggleStore.enabled }
      ]"
    >
      <span class="pe-2">
        Show: {{ dropdownOptions[model].label }}
        <span
          v-if="model !== 'full'"
          class="tw:inline-block tw:w-2 tw:h-2 tw:rounded-full tw:bg-primary tw:ml-1"
          title="Non-default diff view mode"
        />
      </span>
      <SvgIcon
        name="octicon-triangle-down"
        class="dropdown icon tw:inline-block"
        :size="14"
      />
      <div class="ui menu">
        <div
          v-for="option in dropdownOptions"
          :key="option.key"
          class="item"
          @click="handleOptionSelect(option)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
  </div>
</template>
